/**
 * OrderTicket v4 — Interactive order flow with stock search + risk preview.
 * Uses /orders/preview endpoint for guardrail checks.
 */
import { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";

interface Preview {
  symbol: string;
  currentPrice: number;
  capitalUsed: number;
  capitalPct: number;
  riskAtStop: number;
  rewardAtTarget: number;
  rrRatio: number;
  positionCost: number;
  stopLoss: number;
  target: number;
  guardrailWarnings: string[];
  guardrailPass: boolean;
}

interface SearchResult {
  symbol: string;
  close: number;
  volume: number;
}

type OrderSide = "BUY" | "SELL";
type OrderType = "MARKET" | "LIMIT" | "SL" | "SL-M";
type Mode = "VIRTUAL" | "REAL";

function fmt(n: number, decimals = 2) {
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);
}

function GuardLine({ pass, label, value }: { pass: boolean; label: string; value: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "3px 0" }}>
      <span style={{ color: "var(--text-muted)" }}>{label}</span>
      <span style={{ color: pass ? "#00b894" : "#e17055", fontWeight: pass ? 400 : 600 }}>
        {pass ? "Pass" : value}
      </span>
    </div>
  );
}

export default function OrderTicket({ compact = false }: { compact?: boolean }) {
  const [symbol, setSymbol] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [qty, setQty] = useState(10);
  const [price, setPrice] = useState<number>(0);
  const [side, setSide] = useState<OrderSide>("BUY");
  const [orderType, setOrderType] = useState<OrderType>("MARKET");
  const [mode, setMode] = useState<Mode>("VIRTUAL");
  const [sl, setSl] = useState("");
  const [target, setTarget] = useState("");
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(!compact);
  const searchRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Debounced search
  useEffect(() => {
    if (!symbol.trim() || symbol.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.get<{ data: SearchResult[] }>(`/universe/search?q=${encodeURIComponent(symbol)}`);
        setSearchResults(res.data || []);
      } catch { /* */ }
      setSearching(false);
    }, 300);
    return () => clearTimeout(t);
  }, [symbol]);

  // Fetch preview when symbol + qty change
  useEffect(() => {
    if (!symbol.trim() || qty <= 0) { setPreview(null); return; }
    setPreviewLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.post<{ data: Preview }>("/orders/preview", {
          symbol,
          action: side,
          orderType,
          quantity: qty,
          price: orderType !== "MARKET" ? price : undefined,
          stopLoss: sl ? parseFloat(sl) : undefined,
          target: target ? parseFloat(target) : undefined,
        });
        setPreview(res.data);
        if (!sl && res.data.stopLoss) setSl(res.data.stopLoss.toFixed(2));
        if (!target && res.data.target) setTarget(res.data.target.toFixed(2));
        if (!price && res.data.currentPrice) setPrice(res.data.currentPrice);
      } catch { setPreview(null); }
      setPreviewLoading(false);
    }, 500);
    return () => clearTimeout(t);
  }, [symbol, qty, price, sl, target, side, orderType]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!searchRef.current?.contains(e.target as Node) && !dropdownRef.current?.contains(e.target as Node)) {
        setSearchResults([]);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelectSymbol = (sym: string, close?: number) => {
    setSymbol(sym);
    setSearchResults([]);
    if (close) setPrice(close);
  };

  const handleSubmit = async () => {
    if (!symbol.trim()) return;
    setSubmitting(true);
    setMsg(null);
    try {
      await api.post("/orders", {
        symbol: symbol.toUpperCase(),
        action: side,
        orderType,
        quantity: qty,
        price: orderType !== "MARKET" ? parseFloat(price.toString()) : 0,
        triggerPrice: orderType === "SL" || orderType === "SL-M" ? parseFloat(sl) : undefined,
        stopLoss: sl ? parseFloat(sl) : undefined,
        target: target ? parseFloat(target) : undefined,
        mode,
        tag: "manual",
        exchange: "NSE",
        product: "DELIVERY",
      });
      setMsg(`${side} ${symbol.toUpperCase()} placed — virtual order queued`);
      setSymbol(""); setQty(10); setSl(""); setTarget(""); setPreview(null);
    } catch (e: any) {
      setMsg(`Error: ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const preview_ = preview;
  const up = side === "BUY";

  return (
    <div style={{
      background: "var(--surface)",
      border: "1px solid var(--border)",
      borderRadius: 12,
      padding: 16,
      maxWidth: compact ? 400 : 520,
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: expanded ? 16 : 0 }}>
        <span style={{ fontSize: 14, fontWeight: 600 }}>Order Ticket</span>
        {compact && (
          <button onClick={() => setExpanded(!expanded)} style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 12 }}>
            {expanded ? "Collapse" : "Expand"}
          </button>
        )}
      </div>

      {!expanded && !symbol && (
        <div style={{ color: "var(--text-muted)", fontSize: 13 }}>Click expand to place an order</div>
      )}
      {!expanded && symbol && (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 18, fontWeight: 700 }}>{symbol}</span>
          {preview && <span style={{ color: "var(--primary-light)", fontSize: 16 }}>₹{fmt(preview.currentPrice)}</span>}
          <span style={{ fontSize: 12, padding: "2px 8px", borderRadius: 4, background: up ? "rgba(0,184,148,0.15)" : "rgba(225,112,85,0.15)", color: up ? "#00b894" : "#e17055" }}>{side}</span>
        </div>
      )}

      {expanded && (
        <>
          {/* Symbol search */}
          <div style={{ marginBottom: 12, position: "relative" }}>
            <label style={{ fontSize: 11, color: "var(--text-muted)", display: "block", marginBottom: 4 }}>Symbol</label>
            <input
              ref={searchRef}
              type="text"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              placeholder="e.g. RELIANCE"
              style={{
                width: "100%",
                background: "var(--bg)",
                border: "1px solid var(--border)",
                borderRadius: 6,
                padding: "8px 12px",
                color: "var(--text)",
                fontSize: 14,
                fontWeight: 600,
                boxSizing: "border-box",
              }}
            />
            {(searchResults.length > 0 || searching) && (
              <div ref={dropdownRef} style={{
                position: "absolute",
                top: "100%",
                left: 0,
                width: "100%",
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: 6,
                marginTop: 4,
                zIndex: 200,
                boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                maxHeight: 200,
                overflowY: "auto",
              }}>
                {searchResults.map((r) => (
                  <div key={r.symbol} onClick={() => handleSelectSymbol(r.symbol, r.close)}
                    style={{ display: "flex", justifyContent: "space-between", padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid var(--border)", fontSize: 13 }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    <span style={{ fontWeight: 600 }}>{r.symbol}</span>
                    <span style={{ color: "var(--text-muted)" }}>₹{fmt(r.close)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Price + Qty */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 11, color: "var(--text-muted)", display: "block", marginBottom: 4 }}>Qty</label>
              <input type="number" value={qty} onChange={(e) => setQty(parseInt(e.target.value) || 0)} min={1}
                style={{ width: "100%", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, padding: "8px", color: "var(--text)", fontSize: 14, boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: "var(--text-muted)", display: "block", marginBottom: 4 }}>Price</label>
              <input type="number" value={price || ""} onChange={(e) => setPrice(parseFloat(e.target.value) || 0)} placeholder="auto"
                style={{ width: "100%", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, padding: "8px", color: "var(--text)", fontSize: 14, boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: "var(--text-muted)", display: "block", marginBottom: 4 }}>Order Type</label>
              <select value={orderType} onChange={(e) => setOrderType(e.target.value as OrderType)}
                style={{ width: "100%", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, padding: "8px", color: "var(--text)", fontSize: 13, boxSizing: "border-box" }}>
                <option value="MARKET">MARKET</option>
                <option value="LIMIT">LIMIT</option>
                <option value="SL">SL</option>
                <option value="SL-M">SL-M</option>
              </select>
            </div>
          </div>

          {/* SL + Target */}
          {(orderType === "LIMIT" || orderType === "SL" || orderType === "SL-M") && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 11, color: "var(--text-muted)", display: "block", marginBottom: 4 }}>Stop Loss</label>
                <input type="number" value={sl} onChange={(e) => setSl(e.target.value)} placeholder="auto"
                  style={{ width: "100%", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, padding: "8px", color: "var(--text)", fontSize: 14, boxSizing: "border-box" }} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: "var(--text-muted)", display: "block", marginBottom: 4 }}>Target</label>
                <input type="number" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="auto"
                  style={{ width: "100%", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, padding: "8px", color: "var(--text)", fontSize: 14, boxSizing: "border-box" }} />
              </div>
            </div>
          )}

          {/* Side + Mode */}
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            {(["BUY", "SELL"] as const).map((s) => (
              <button key={s} onClick={() => setSide(s)}
                style={{
                  flex: 1,
                  padding: "8px",
                  borderRadius: 6,
                  border: "1px solid",
                  borderColor: side === s ? (s === "BUY" ? "#00b894" : "#e17055") : "var(--border)",
                  background: side === s ? (s === "BUY" ? "rgba(0,184,148,0.15)" : "rgba(225,112,85,0.15)") : "transparent",
                  color: side === s ? (s === "BUY" ? "#00b894" : "#e17055") : "var(--text-muted)",
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: "pointer",
                }}>
                {s}
              </button>
            ))}
            <button onClick={() => setMode(mode === "VIRTUAL" ? "REAL" : "VIRTUAL")}
              style={{
                padding: "8px 12px",
                borderRadius: 6,
                border: "1px solid",
                borderColor: mode === "VIRTUAL" ? "#00b894" : "#e17055",
                background: mode === "VIRTUAL" ? "rgba(0,184,148,0.1)" : "rgba(225,112,85,0.1)",
                color: mode === "VIRTUAL" ? "#00b894" : "#e17055",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}>
              {mode}
            </button>
          </div>

          {/* Risk Preview */}
          {previewLoading && (
            <div style={{ padding: 12, textAlign: "center", color: "var(--text-muted)", fontSize: 12, marginBottom: 12 }}>
              Checking risk...
            </div>
          )}

          {preview_ && !previewLoading && (
            <div style={{ background: "var(--bg)", borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, paddingBottom: 8, borderBottom: "1px solid var(--border)" }}>
                <span style={{ color: "var(--text-muted)" }}>Capital used</span>
                <span style={{ fontWeight: 600 }}>₹{fmt(preview_.capitalUsed)} ({preview_.capitalPct}% NAV)</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ color: "var(--text-muted)", fontSize: 10 }}>Risk at SL</div>
                  <div style={{ color: "#e17055", fontWeight: 600 }}>₹{fmt(preview_.riskAtStop)}</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ color: "var(--text-muted)", fontSize: 10 }}>Reward</div>
                  <div style={{ color: "#00b894", fontWeight: 600 }}>₹{fmt(preview_.rewardAtTarget)}</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ color: "var(--text-muted)", fontSize: 10 }}>R:R</div>
                  <div style={{ fontWeight: 600 }}>{preview_.rrRatio.toFixed(2)}</div>
                </div>
              </div>

              <div style={{ marginBottom: 4 }}>
                  <GuardLine pass={preview_.guardrailPass} label="Position < 10% NAV" value={`${preview_.capitalPct.toFixed(1)}%`} />
                <GuardLine pass={true} label="Margin available" value="OK" />
              </div>

              {preview_.guardrailWarnings.map((w, i) => (
                <div key={i} style={{ color: "#e17055", fontSize: 11, padding: "2px 0" }}>⚠ {w}</div>
              ))}

              <div style={{ marginTop: 8, padding: "6px 8px", background: preview_.guardrailPass ? "rgba(0,184,148,0.1)" : "rgba(225,112,85,0.1)", borderRadius: 4, textAlign: "center", fontSize: 11, fontWeight: 600, color: preview_.guardrailPass ? "#00b894" : "#e17055" }}>
                {preview_.guardrailPass ? "All checks passed — ready to place" : "Guardrail warning — review before placing"}
              </div>
            </div>
          )}

          {/* Place Order */}
          <button
            onClick={handleSubmit}
            disabled={submitting || !symbol.trim() || qty <= 0}
            style={{
              width: "100%",
              padding: "12px",
              borderRadius: 8,
              border: "none",
              background: (up ? "#00b894" : "#e17055"),
              color: "white",
              fontSize: 15,
              fontWeight: 700,
              cursor: submitting || !symbol.trim() || qty <= 0 ? "not-allowed" : "pointer",
              opacity: submitting || !symbol.trim() || qty <= 0 ? 0.5 : 1,
            }}
          >
            {submitting ? "Placing..." : `Place ${side} Order — ${mode}`}
          </button>

          {msg && (
            <div style={{ marginTop: 8, padding: "8px 12px", background: msg.includes("Error") ? "rgba(225,112,85,0.1)" : "rgba(0,184,148,0.1)", borderRadius: 6, fontSize: 12, color: msg.includes("Error") ? "#e17055" : "#00b894", textAlign: "center" }}>
              {msg}
            </div>
          )}
        </>
      )}
    </div>
  );
}
